import type { UpdateData } from '#src/js/WebsocketUpdateModule.mts'
import { useWebsocketUpdates } from '#src/js/WebsocketUpdateModule.mts'
import type { RoomMeta } from '#src/js/worker/Room.mts'
import type { MaybeRef, Ref } from 'vue'
import { onBeforeUnmount, provide, ref, unref, watch } from 'vue'

export const INJECTION_SYMBOL = Symbol('UpdateSource')

type RegisterPathCallback = ReturnType<typeof useWebsocketUpdates>["registerPath"]
type UnregisterPathCallback = ReturnType<typeof useWebsocketUpdates>["unregisterPath"]

export type ProvidedUpdateData<Data extends UpdateData<any> | undefined> = {
  pathRef: Ref<string | undefined>,
  updateDataRef: Ref<Data>,
}
export type UpdateMeta = RoomMeta

export type UpdateDataWrapper<Sections extends any, Static = any> = UpdateData<Sections,Static> | undefined

function _generateUpdatePathFunction<Sections extends any>(registerPath: RegisterPathCallback, unregisterPath: UnregisterPathCallback) {
  const updatePath = (newPath: string | undefined, oldPath: string | undefined, updateData: Ref<UpdateDataWrapper<Sections>>) => {

    if (newPath === oldPath) {
      return
    }

    if (oldPath !== undefined) {
      unregisterPath(oldPath)
      updateData.value = undefined
    }

    if (newPath !== undefined) {
      const newData = registerPath(newPath) as UpdateData<Sections>
      updateData.value = newData
    }
  }

  return updatePath
}

/**
 *
 * @param updateElementRef an Element that should have attachBehaviours and similar logic applied to it on updates.
 * @param explicitPath a ref to a the socket url.
 * @returns
 */
export function useUpdateSource<Sections, Static = any> (pathRef: MaybeRef<string | undefined>): { updateData: Ref<UpdateDataWrapper<Sections, Static>> } {
  // const store = useStore()
  type Data = UpdateDataWrapper<Sections, Static>

  const updateDataRef: Ref<Data> = ref(undefined)

  const { registerPath, unregisterPath, } = useWebsocketUpdates()
  const doUpdatePath = _generateUpdatePathFunction(registerPath, unregisterPath)

  if (typeof pathRef == 'string') {
    updateDataRef.value = registerPath(pathRef)
  } else {
    const _pathRef = pathRef as Ref<string | undefined>;
    watch(_pathRef, (newPath, oldPath) => {
      console.log("useUpdateSource updating path", { newPath, oldPath, updateDataRef, })
      doUpdatePath(newPath, oldPath, updateDataRef)
    }, { immediate: true, flush: 'pre' })
  }

  onBeforeUnmount(() => {
    const _current = unref(pathRef)
    if (_current) {
      unregisterPath(_current)
    }
  })

  if (import.meta.env.DEV) {
    watch(updateDataRef, () => {
      console.debug("useUpdateSource updating updateDataRef", unref(pathRef), unref(updateDataRef))
    })
  }

  provide(INJECTION_SYMBOL, { pathRef, updateDataRef, })

  return { updateData: updateDataRef }
}
